import { required, useListContext, useTranslate } from "ra-core";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { CreateButton } from "@/components/admin/create-button";
import { Create } from "@/components/admin/create";
import { DataTable } from "@/components/admin/data-table";
import { DateInput } from "@/components/admin/date-input";
import { Edit } from "@/components/admin/edit";
import { List } from "@/components/admin/list";
import { MobileRecordCard } from "@/components/admin/mobile-record-card";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextInput } from "@/components/admin/text-input";
import { Card, CardContent } from "@/components/ui/card";

import { TopToolbar } from "../../atomic-crm/layout/TopToolbar";
import {
  SaleAvatar,
  saleName,
  todayIso,
  useFormatDate,
  useSales,
} from "../common/components";

const TimeTotal = () => {
  const { data } = useListContext();
  const translate = useTranslate();
  const total = (data ?? []).reduce((sum, e) => sum + Number(e.hours), 0);
  return (
    <p className="mb-2 text-sm text-muted-foreground">
      {translate("hub.time.total", { hours: Math.round(total * 100) / 100 })}
    </p>
  );
};

const TimeList = () => {
  const translate = useTranslate();
  const formatDate = useFormatDate();
  const { data: sales } = useSales();
  const filters = [
    <SelectInput
      key="sales"
      source="sales_id"
      choices={(sales ?? []).map((s) => ({ id: s.id, name: saleName(s) }))}
      translateChoice={false}
      emptyText="hub.common.all"
      alwaysOn
    />,
    <ReferenceInput key="deal" source="deal_id" reference="deals" alwaysOn>
      <AutocompleteInput
        label="resources.time_entries.fields.deal_id"
        optionText="name"
      />
    </ReferenceInput>,
    <DateInput
      key="from"
      source="date@gte"
      label="resources.time_entries.fields.date"
    />,
  ];
  return (
    <List
      resource="time_entries"
      filters={filters}
      sort={{ field: "date", order: "DESC" }}
      perPage={100}
      actions={
        <TopToolbar>
          <CreateButton label="hub.time.new" />
        </TopToolbar>
      }
    >
      <TimeTotal />
      <DataTable
        rowClick="edit"
        bulkActionButtons={false}
        mobileCard={(e) => (
          <MobileRecordCard
            leading={<SaleAvatar saleId={e.sales_id} className="size-8" />}
            title={
              e.description ||
              (e.task_id != null ? (
                <ReferenceField
                  source="task_id"
                  reference="team_tasks"
                  link={false}
                />
              ) : (
                formatDate(e.date)
              ))
            }
            trailing={
              <span className="text-sm font-semibold tabular-nums">
                {`${Number(e.hours)} h`}
              </span>
            }
            subtitle={
              e.deal_id != null ? (
                <ReferenceField
                  source="deal_id"
                  reference="deals"
                  link={false}
                />
              ) : null
            }
            meta={[
              formatDate(e.date),
              e.billable
                ? translate("resources.time_entries.fields.billable")
                : null,
            ]}
          />
        )}
      >
        <DataTable.Col source="date" render={(e) => formatDate(e.date)} />
        <DataTable.Col
          source="sales_id"
          render={(e) => <SaleAvatar saleId={e.sales_id} />}
        />
        <DataTable.Col source="hours" />
        <DataTable.Col source="task_id">
          <ReferenceField source="task_id" reference="team_tasks" />
        </DataTable.Col>
        <DataTable.Col source="deal_id">
          <ReferenceField source="deal_id" reference="deals" link="show" />
        </DataTable.Col>
        <DataTable.Col source="description" />
        <DataTable.Col
          source="billable"
          render={(e) => (e.billable ? "✓" : "")}
        />
      </DataTable>
    </List>
  );
};

const TimeInputs = () => (
  <div className="flex flex-col gap-4">
    <div className="grid gap-4 sm:grid-cols-3 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <DateInput source="date" validate={required()} />
      <NumberInput
        source="hours"
        step={0.25}
        min={0.25}
        validate={required()}
      />
      <BooleanInput source="billable" />
    </div>
    <div className="grid gap-4 sm:grid-cols-2 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <ReferenceInput source="task_id" reference="team_tasks">
        <AutocompleteInput
          label="resources.time_entries.fields.task_id"
          optionText="title"
        />
      </ReferenceInput>
      <ReferenceInput source="deal_id" reference="deals">
        <AutocompleteInput
          label="resources.time_entries.fields.deal_id"
          optionText="name"
        />
      </ReferenceInput>
    </div>
    <TextInput source="description" />
  </div>
);

const TimeCreate = () => (
  <Create
    resource="time_entries"
    redirect="list"
    className="mx-auto w-full max-w-3xl"
  >
    <Card>
      <CardContent>
        <SimpleForm
          className="max-w-none"
          defaultValues={{ date: todayIso(), billable: true }}
        >
          <TimeInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Create>
);

const TimeEdit = () => (
  <Edit
    resource="time_entries"
    redirect="list"
    mutationMode="pessimistic"
    className="mx-auto w-full max-w-3xl"
  >
    <Card>
      <CardContent>
        <SimpleForm className="max-w-none">
          <TimeInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Edit>
);

export default { list: TimeList, create: TimeCreate, edit: TimeEdit };
